import React, { useState } from 'react';
import { CalculatorTab } from '@/components/CalculatorTab';
import { BasicPercentage } from '@/components/calculators/BasicPercentage';
import { PercentageOf } from '@/components/calculators/PercentageOf';
import { PercentageChange } from '@/components/calculators/PercentageChange';
import { DiscountCalculator } from '@/components/calculators/DiscountCalculator';
import { TipCalculator } from '@/components/calculators/TipCalculator';
import { AdPlaceholder } from '@/components/AdPlaceholder';
import { UserMenu } from '@/components/UserMenu';
import { PremiumUpgrade } from '@/components/premium/PremiumUpgrade';
import { usePremium } from '@/hooks/usePremium';
import { Calculator } from 'lucide-react';

const calculatorTabs = [
  { id: 'basic', label: 'What is X% of Y?', component: BasicPercentage },
  { id: 'percentage-of', label: 'X is what % of Y?', component: PercentageOf },
  { id: 'change', label: 'Percentage Change', component: PercentageChange },
  { id: 'discount', label: 'Discount Calculator', component: DiscountCalculator },
  { id: 'tip', label: 'Tip Calculator', component: TipCalculator },
];

const Index = () => {
  const [activeTab, setActiveTab] = useState('basic');
  const { isPremium } = usePremium();

  const ActiveComponent = calculatorTabs.find(tab => tab.id === activeTab)?.component || BasicPercentage;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-calculator-gradient rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-calculator-gradient bg-clip-text text-transparent">
                  Percentage Calculator
                </h1>
                <p className="text-muted-foreground">
                  Solve common percentage problems with ease
                </p>
              </div>
            </div>
            <UserMenu />
          </div>
        </div>
        
        {/* Banner Ad - Below Header */}
        {!isPremium && (
          <div className="py-6">
            <AdPlaceholder type="banner" />
          </div>
        )}
      </header>

      {/* Tab Navigation */}
      <div className="border-b border-border bg-card/50">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide">
            {calculatorTabs.map((tab) => (
              <CalculatorTab
                key={tab.id}
                id={tab.id}
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={setActiveTab}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Calculator Content with Sidebar Layout */}
      <div className="flex flex-col lg:flex-row gap-8 container mx-auto px-4 py-8">
        {/* Main Content */}
        <main className="flex-1 max-w-2xl mx-auto lg:mx-0">
          <ActiveComponent />
        </main>

        {/* Desktop Sidebar - Skyscraper Ad or Premium Upgrade */}
        <aside className="hidden lg:block w-80 flex-shrink-0">
          <div className="sticky top-8 space-y-6">
            {!isPremium ? (
              <>
                <PremiumUpgrade />
                <AdPlaceholder type="skyscraper" />
              </>
            ) : (
              <div className="text-center p-6 bg-primary/5 rounded-lg border border-primary/20">
                <h3 className="font-semibold text-primary mb-2">Premium Active</h3>
                <p className="text-sm text-muted-foreground">
                  Enjoy your ad-free experience and premium features!
                </p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Get answers to common percentage calculation questions
          </p>
        </div>

        <div className="grid gap-8 md:gap-12">
          {/* How do I calculate a percentage? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              How do I calculate a percentage?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                To calculate a percentage, use the formula: <strong>(Part ÷ Whole) × 100</strong>
              </p>
              <p>
                For example, if you scored 85 out of 100 on a test: (85 ÷ 100) × 100 = 85%
              </p>
              <p>
                You can also calculate what percentage one number is of another using our "X is what % of Y?" calculator above.
              </p>
            </div>
          </div>

          {/* How do I calculate a percentage increase? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              How do I calculate a percentage increase?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                To calculate percentage increase, use: <strong>((New Value - Original Value) ÷ Original Value) × 100</strong>
              </p>
              <p>
                For example, if a price increased from $50 to $65: ((65 - 50) ÷ 50) × 100 = 30% increase
              </p>
              <p>
                Use our "Percentage Change" calculator to automatically determine if it's an increase or decrease.
              </p>
            </div>
          </div>

          {/* How do I calculate a discount? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              How do I calculate a discount?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                To calculate a discount, multiply the original price by the discount percentage: <strong>Original Price × (Discount % ÷ 100)</strong>
              </p>
              <p>
                For a $100 item with a 25% discount: $100 × (25 ÷ 100) = $25 discount, final price = $75
              </p>
              <p>
                Our Discount Calculator above shows both the final price and amount saved automatically.
              </p>
            </div>
          </div>

          {/* How much should I tip? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              How much should I tip?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                Tipping guidelines vary by service and location:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Restaurants:</strong> 15-20% for good service, 20%+ for excellent service</li>
                <li><strong>Food delivery:</strong> 10-15% minimum, more for bad weather or long distances</li>
                <li><strong>Bars:</strong> $1-2 per drink or 15-20% of total bill</li>
                <li><strong>Hair salons:</strong> 15-20% of service cost</li>
                <li><strong>Taxi/Uber:</strong> 10-15% of fare</li>
              </ul>
              <p>
                Use our Tip Calculator to easily calculate tips and split bills among multiple people.
              </p>
            </div>
          </div>

          {/* What is the formula for percentage of a number? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              What is the formula for percentage of a number?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                To find a percentage of a number, use: <strong>Number × (Percentage ÷ 100)</strong>
              </p>
              <p>
                For example, to find 25% of 80: 80 × (25 ÷ 100) = 80 × 0.25 = 20
              </p>
              <p>
                This is the same as multiplying the number by the decimal form of the percentage (25% = 0.25).
              </p>
            </div>
          </div>

          {/* How do I convert a decimal to a percentage? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              How do I convert a decimal to a percentage?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                To convert a decimal to a percentage, <strong>multiply by 100 and add the % symbol</strong>.
              </p>
              <p>
                Examples: 0.75 = 75%, 0.5 = 50%, 1.25 = 125%, 0.03 = 3%
              </p>
              <p>
                To convert a percentage to a decimal, divide by 100: 25% = 0.25, 150% = 1.5
              </p>
            </div>
          </div>

          {/* What is a percentage point? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              What is the difference between percentage and percentage points?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                A <strong>percentage point</strong> is the unit for the arithmetic difference between percentages.
              </p>
              <p>
                Example: If interest rates increase from 3% to 5%, that's:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>A 2 percentage point increase (5% - 3% = 2 percentage points)</li>
                <li>A 66.7% relative increase ((5-3)/3 × 100 = 66.7%)</li>
              </ul>
              <p>
                Percentage points measure absolute change, while percentages measure relative change.
              </p>
            </div>
          </div>

          {/* How do I calculate compound percentage? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              How do I calculate compound percentage changes?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                For compound percentage changes, multiply the factors: <strong>Original × (1 + rate₁) × (1 + rate₂) × ...</strong>
              </p>
              <p>
                Example: $100 with +10% then +5% growth: $100 × 1.10 × 1.05 = $115.50
              </p>
              <p>
                This is different from adding percentages (10% + 5% ≠ 15% compound growth).
              </p>
            </div>
          </div>

          {/* When should I use percentage vs ratio? */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-foreground">
              When should I use percentages vs ratios?
            </h3>
            <div className="text-muted-foreground space-y-3">
              <p>
                <strong>Use percentages when:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Comparing parts to a whole (75% of students passed)</li>
                <li>Expressing rates or changes (5% interest rate)</li>
                <li>Making comparisons easier to understand</li>
              </ul>
              <p>
                <strong>Use ratios when:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Comparing two quantities directly (3:1 ratio)</li>
                <li>Showing proportional relationships</li>
                <li>Recipe measurements or mixing instructions</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Mobile Banner Ad - End of FAQ */}
        {!isPremium && (
          <div className="lg:hidden mt-12 pt-8 border-t border-border">
            <AdPlaceholder type="banner" />
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card mt-16">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-sm text-muted-foreground">
            Built with precision for accurate percentage calculations
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;